import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";
import {
  getGetReportSectionQueryKey,
  getReportSection,
  useGetCustomReport,
  useGetTemplate,
} from "~/api/custom-reports/custom-reports";
import {
  ReportSectionStatus,
  ReportStatus,
  type Section,
  TemplateStatus,
} from "~/api/model";
import { truncatePrompt } from "~/features/cases/utils/promptPreview";

const TEMPLATE_POLL_INTERVAL_MS = 3000;
const REPORT_POLL_INTERVAL_MS = 3000;

export type SectionViewStatus =
  | "splitting"
  | "pending"
  | "processing"
  | "completed"
  | "failed";

export interface SectionView {
  id: string;
  order: number;
  title: string;
  promptPreview: string;
  fullPrompt: string;
  status: SectionViewStatus;
  content: string | null;
}

export type TemplateSectionsPhase =
  | "reading"
  | "splitting"
  | "sections_ready"
  | "generating"
  | "ready"
  | "failed";

export interface TemplateSectionsView {
  phase: TemplateSectionsPhase;
  sections: SectionView[];
  totalSections: number;
  completedCount: number;
}

interface ReportSectionLookup {
  status: SectionViewStatus | undefined;
  id: string | undefined;
}

/**
 * Match a template section to its corresponding report section meta.
 * We prefer matching by `order` (stable across both DTOs) and fall back
 * to a case-insensitive title comparison when the order isn't unique
 * or doesn't line up.
 */
function findReportSection(
  templateSection: Section,
  reportSections: ReadonlyArray<{
    id?: string;
    order?: number;
    title?: string;
    status?: string;
  }>
): ReportSectionLookup {
  const byOrder = reportSections.find((s) => s.order === templateSection.order);
  if (byOrder) {
    return {
      id: byOrder.id,
      status: byOrder.status as SectionViewStatus | undefined,
    };
  }

  const normalizedTitle = templateSection.title.toLowerCase().trim();
  const byTitle = reportSections.find(
    (s) => s.title?.toLowerCase().trim() === normalizedTitle
  );
  if (byTitle) {
    return {
      id: byTitle.id,
      status: byTitle.status as SectionViewStatus | undefined,
    };
  }

  return { id: undefined, status: undefined };
}

const TERMINAL_REPORT_STATUSES = new Set<string>([
  ReportStatus.completed,
  ReportStatus.failed,
]);

const TERMINAL_SECTION_STATUSES = new Set<string>([
  ReportSectionStatus.completed,
  ReportSectionStatus.failed,
]);

/**
 * Merge the template (with its sections) and the demo report (with per-section
 * statuses and content) into a single view-model used by the FTUE progress UI.
 *
 * Phase derivation:
 * - `reading` while the template is still processing and no sections have appeared yet.
 * - `splitting` once sections start appearing but the template isn't `completed`.
 * - `sections_ready` when the template is ready but the demo report hasn't started
 *   producing per-section status yet.
 * - `generating` when the report has at least one non-`pending` section.
 * - `ready` when the report itself is `completed`.
 * - `failed` when either the template or the report is in a failed state.
 */
export const useFtueTemplateSectionsView = (
  organizationId: string,
  templateId: string | null,
  caseId: string | null,
  reportId: string | null
): TemplateSectionsView => {
  const isTemplateEnabled = !!organizationId && !!templateId;

  const { data: templateData } = useGetTemplate(
    organizationId,
    templateId ?? "",
    {
      query: {
        enabled: isTemplateEnabled,
        refetchInterval: (query) => {
          const status = query.state.data?.status;
          if (
            status === TemplateStatus.completed ||
            status === TemplateStatus.failed
          ) {
            return false;
          }
          return TEMPLATE_POLL_INTERVAL_MS;
        },
        refetchIntervalInBackground: true,
      },
    }
  );

  const isReportEnabled = !!organizationId && !!caseId && !!reportId;

  const { data: reportData } = useGetCustomReport(
    organizationId,
    caseId ?? "",
    reportId ?? "",
    {
      query: {
        enabled: isReportEnabled,
        refetchInterval: (query) => {
          const data = query.state.data;
          if (!data) return REPORT_POLL_INTERVAL_MS;

          const reportTerminal = TERMINAL_REPORT_STATUSES.has(data.status);
          const sections = data.reportSections ?? [];
          const sectionsTerminal =
            sections.length > 0 &&
            sections.every((s) => TERMINAL_SECTION_STATUSES.has(s.status));

          if (reportTerminal && (sections.length === 0 || sectionsTerminal)) {
            return false;
          }
          return REPORT_POLL_INTERVAL_MS;
        },
        refetchIntervalInBackground: true,
      },
    }
  );

  const templateSections = useMemo<Section[]>(() => {
    const raw = templateData?.sections ?? [];
    return [...raw].sort((a, b) => a.order - b.order);
  }, [templateData?.sections]);

  const reportSections = useMemo(
    () => reportData?.reportSections ?? [],
    [reportData?.reportSections]
  );

  // Resolve, for each template section, the matching report section id (if any).
  // We need stable references for the `useQueries` invocations below.
  const reportSectionByTemplateSection = useMemo(
    () => templateSections.map((s) => findReportSection(s, reportSections)),
    [templateSections, reportSections]
  );

  const sectionContentQueries = useQueries({
    queries: reportSectionByTemplateSection.map((match) => ({
      queryKey: getGetReportSectionQueryKey(
        organizationId,
        caseId ?? "",
        reportId ?? "",
        match.id ?? ""
      ),
      queryFn: ({ signal }: { signal?: AbortSignal }) =>
        getReportSection(
          organizationId,
          caseId ?? "",
          reportId ?? "",
          match.id ?? "",
          undefined,
          signal
        ),
      enabled:
        isReportEnabled &&
        !!match.id &&
        match.status === ReportSectionStatus.completed,
      staleTime: Infinity,
    })),
  });

  const templateStatus = templateData?.status;
  const reportStatus = reportData?.status;

  const phase = derivePhase({
    templateStatus,
    templateSectionsCount: templateSections.length,
    reportStatus,
    reportSections,
  });

  const sections = useMemo<SectionView[]>(() => {
    return templateSections.map((templateSection, idx) => {
      const match = reportSectionByTemplateSection[idx];
      const fullPrompt = templateSection.prompt?.trim()
        ? templateSection.prompt
        : (templateSection.purpose ?? "");

      const status = deriveSectionStatus(phase, match.status);
      const content =
        status === "completed"
          ? (sectionContentQueries[idx]?.data?.content?.trim() ?? null)
          : null;

      return {
        id: templateSection.id,
        order: templateSection.order,
        title: templateSection.title,
        promptPreview: truncatePrompt(fullPrompt),
        fullPrompt,
        status,
        content,
      };
    });
  }, [
    templateSections,
    reportSectionByTemplateSection,
    sectionContentQueries,
    phase,
  ]);

  const completedCount = sections.reduce(
    (acc, s) => acc + (s.status === "completed" ? 1 : 0),
    0
  );

  return {
    phase,
    sections,
    totalSections: sections.length,
    completedCount,
  };
};

function derivePhase(args: {
  templateStatus: string | undefined;
  templateSectionsCount: number;
  reportStatus: string | undefined;
  reportSections: ReadonlyArray<{ status?: string }>;
}): TemplateSectionsPhase {
  const {
    templateStatus,
    templateSectionsCount,
    reportStatus,
    reportSections,
  } = args;

  if (templateStatus === TemplateStatus.failed) return "failed";
  if (reportStatus === ReportStatus.failed) return "failed";

  if (reportStatus === ReportStatus.completed) return "ready";

  const hasAnyActiveReportSection = reportSections.some(
    (s) => s.status && s.status !== ReportSectionStatus.pending
  );
  if (hasAnyActiveReportSection) return "generating";

  if (templateStatus === TemplateStatus.completed) {
    return "sections_ready";
  }

  if (templateSectionsCount > 0) return "splitting";

  return "reading";
}

function deriveSectionStatus(
  phase: TemplateSectionsPhase,
  reportSectionStatus: SectionViewStatus | undefined
): SectionViewStatus {
  if (phase === "splitting") return "splitting";
  if (phase === "reading") return "pending";

  if (
    reportSectionStatus === "processing" ||
    reportSectionStatus === "completed" ||
    reportSectionStatus === "failed"
  ) {
    return reportSectionStatus;
  }

  // `pending` from the report, or no report data yet.
  return "pending";
}
